import axios from 'axios';
import { definirAccessToken, limparSessao, obterAccessToken } from './session';

/**
 * O cliente HTTP do backend V2.
 *
 * `baseURL` vazio por padrão, e é de propósito: em desenvolvimento o `devServer`
 * do Vue repassa `/api` para a porta 3001 (ver `vue.config.js`), então o
 * navegador enxerga tudo na mesma origem. Isso não é conveniência — o cookie de
 * refresh é `SameSite=Strict`, e o navegador simplesmente não o envia numa
 * chamada de 8080 para 3001. Apontar direto para `http://localhost:3001` faz o
 * login funcionar e a renovação falhar em silêncio meia hora depois.
 */
const BASE = process.env.VUE_APP_API_V2_URL || '';

const api = axios.create({
  baseURL: BASE,
  // Necessário para o cookie de refresh ir e voltar.
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = obterAccessToken();
  if (token) {
    config.headers = { ...config.headers, Authorization: `Bearer ${token}` };
  }
  return config;
});

/**
 * Uma renovação de cada vez.
 *
 * O boot do cliente dispara várias chamadas em paralelo. Se o access token
 * expirou, todas voltam 401 juntas — e sem esta trava cada uma pediria o seu
 * refresh. Como o refresh é **rotativo**, o segundo pedido chega com um token
 * que o primeiro já invalidou, e o backend trata reuso de refresh como sessão
 * comprometida: derruba a família inteira. O jogador seria deslogado justamente
 * por ter aberto o jogo.
 */
let renovacaoEmCurso = null;

function renovar() {
  if (!renovacaoEmCurso) {
    renovacaoEmCurso = axios
      .post(`${BASE}/api/auth/refresh`, {}, { withCredentials: true })
      .then(({ data }) => {
        definirAccessToken(data.accessToken);
        return data.accessToken;
      })
      .finally(() => {
        renovacaoEmCurso = null;
      });
  }
  return renovacaoEmCurso;
}

api.interceptors.response.use(
  (resposta) => resposta,
  async (erro) => {
    const original = erro.config;
    const ehRotaDeSessao =
      original?.url?.includes('/api/auth/login') ||
      original?.url?.includes('/api/auth/register') ||
      original?.url?.includes('/api/auth/refresh');

    // `_jaTentou` impede o laço: se a chamada repetida também der 401, o refresh
    // não resolve e insistir só multiplica requisição.
    if (erro.response?.status !== 401 || ehRotaDeSessao || original?._jaTentou) {
      return Promise.reject(erro);
    }

    original._jaTentou = true;
    try {
      await renovar();
      return api(original);
    } catch (falha) {
      limparSessao();
      return Promise.reject(falha);
    }
  },
);

export default api;
export { renovar };
